import {Realm, Region, RegionToRealmMap} from 'oci-console-regions';

const ENDPOINT_VERSION_1 = 'v1';
const ENDPOINT_VERSION_20160918 = '20160918';
const SERVICE_LOGIN = 'login';
const SERVICE_CONSOLE = 'ocistatus';
const SERVICE_IDENTITY = 'identity';
const SERVICE_AUTHENTICATION = 'auth';

export const mustNotEmptyNullOrUndefined = (
  name: string,
  value: any,
  errorMessage?: string,
) => {
  if (!value) {
    throw new Error(
      errorMessage || `${name} must not be empty, null or undefined.`,
    );
  }
};

export const ConsoleDefaultRegionMapping = new Map<Realm, Region>([
  [Realm.OC1, Region.PHX],
  [Realm.OC2, Region.LFI],
  [Realm.OC3, Region.RIC],
  [Realm.OC4, Region.LTN],
  [Realm.OC5, Region.TIW],
  [Realm.OC6, Region.FTW],
  [Realm.OC7, Region.BWI],
  [Realm.OC8, Region.NJA],
  [Realm.OC9, Region.MCT],
  [Realm.OC10, Region.WGA],
  [Realm.OC11, Region.DAL],
  [Realm.OC12, Region.GEU],
  [Realm.OC14, Region.BGY],
  [Realm.OC16, Region.SGU],
  [Realm.OC25, Region.TYO],
  [Realm.OC15, Region.DAC],
  [Realm.OC40, Region.JBB],
  [Realm.OC50, Region.MFQ],
  [Realm.OC51, Region.JSK],
]);

const realmTopLevels = new Map<Realm, string>([
  [Realm.R1, 'oracleiaas.com'],
  [Realm.OC1, 'oraclecloud.com'],
  [Realm.OC2, 'oraclegovcloud.com'],
  [Realm.OC3, 'oraclegovcloud.com'],
  [Realm.OC4, 'oraclegovcloud.uk'],
  [Realm.OC5, 'oraclecloud5.com'],
  [Realm.OC6, 'oraclecloud.ic.gov'],
  [Realm.OC7, 'oc.ic.gov'],
  [Realm.OC8, 'oraclecloud8.com'],
  [Realm.OC9, 'oraclecloud9.com'],
  [Realm.OC10, 'oraclecloud10.com'],
  [Realm.OC11, 'oraclecloud11.com'],
  [Realm.OC12, 'oraclecloud12.com'],
  [Realm.OC14, 'oraclecloud14.com'],
  [Realm.OC16, 'oraclecloud16.com'],
  [Realm.OC25, 'nricloud.jp'],
  [Realm.OC15, 'oraclecloud15.com'],
  [Realm.OC40, 'jpsovereigncloud.jp'],
  [Realm.OC50, 'opencanvas.jp'],
  [Realm.OC51, 'oraclecloud51.com'],
]);

export const consoleBaseURLs = new Map<Realm, string>([
  [Realm.OC1, 'cloud.oracle.com'],
  [Realm.OC2, 'cloud.oracle.com'], // OC2 is hosted in OC1
  [Realm.OC5, 'https://console.us-tacoma-1.oraclecloud5.com'],
  [Realm.OC6, 'https://console.us-gov-fortworth-1.oraclecloud.com'],
  [Realm.OC8, 'https://oc8.cloud.oracle.com'],
  [Realm.OC11, 'https://console.us-gov-fortworth-3.oraclecloud.com'],
  [Realm.OC16, 'https://oc16.cloud.oracle.com'],
  [Realm.OC17, 'https://oc17.cloud.oracle.com'],
  [Realm.OC22, 'https://cloud.psn-pco.it'],
  [Realm.OC25, 'https://console.ap-dcc-tokyo-1.nricloud.jp'],
  [Realm.OC26, 'https://oc26.cloud.oracle.com'],
  [Realm.OC31, 'https://cloud.sovereigncloud.nz'],
  // [Realm.OC29, 'TBA'], // To be added
  [Realm.OC35, 'https://oc35.cloud.oracle.com'],
  // [Realm.OC36, 'TBA'], // To be Added
  [Realm.OC40, 'https://cloud.jpsovereigncloud.jp'],
  [Realm.OC41, 'https://cloud.dutechcloud.ae'],
  // [Realm.OC43, ''], // To be added
  [Realm.OC50, 'https://cloud.opencanvas.jp'],
  [Realm.OC51, 'https://cloud.oraclecloud51.com'],
  [Realm.OC15, 'https://cloud.oraclecloud15.com'],
  // [Realm.OC54, 'https://cloud.portalclarocco.com.br'],// Not in regions list yet
  // [Realm.OC48, 'TBA'], // No in region list yet
  // [Realm.OC37, 'TBA'], // No in region list yet
  // [Realm.OC53, 'TBA'], // No in region list yet
  // [Realm.OC55, 'TBA'], // No in region list yet
]);

export const hostnameToRealm = new Map<string, Realm>([
  ['localhost', Realm.OC1], // Update as needed
  ['staging.ocistatus.oci.oraclecloud.com', Realm.OC1],
  ['ocistatus-stg.plugins.oci.oraclecloud.com', Realm.OC1],
  ['dev.ocistatus.oci.oraclecloud.com', Realm.OC1],
  ['ocistatus-dev.plugins.oci.oraclecloud.com', Realm.OC1],
  ['ocistatus.plugins.oci.oraclecloud.com', Realm.OC1],
  ['ocistatus.oci.oraclecloud.com', Realm.OC1],
  ['ocistatus.oraclecloud.com', Realm.OC1],

  ['gov.staging.ocistatus.oci.oraclecloud.com', Realm.OC2],
  ['gov.dev.ocistatus.oci.oraclecloud.com', Realm.OC2],
  ['gov.ocistatus.oci.oraclecloud.com', Realm.OC2],
  ['gov.ocistatus.com', Realm.OC2],

  ['ocistatus.plugins.oci.oraclecloud5.com', Realm.OC5],
  ['ocistatus.us-tacoma-1.oci.oraclecloud5.com', Realm.OC5],

  ['ocistatus.oci.oraclecloud.ic.gov', Realm.OC6],
  ['ocistatus.plugins.oci.oraclecloud.ic.gov', Realm.OC6],
  ['ocistatus.us-gov-fortworth-1.oci.oraclecloud.ic.gov', Realm.OC6],
  ['ocistatus.us-gov-sterling-2.oci.oraclecloud.ic.gov', Realm.OC6],

  ['oc8.ocistatus.com', Realm.OC8],
  ['ocistatus.ap-chiyoda-1.oci.oraclecloud8.com', Realm.OC8],

  ['ocistatus.oci.oraclecloud.smil.mil', Realm.OC11],
  ['ocistatus.us-gov-fortworth-3.oci.oraclecloud.smil.mil', Realm.OC11],
  ['ocistatus.us-gov-sterling-3.oci.oraclecloud.smil.mil', Realm.OC11],

  ['ocistatus.oci.oraclecloud16.com', Realm.OC16],
  ['ocistatus.us-westjordan-1.oci.oraclecloud16.com', Realm.OC16],

  ['ocistatus.oci.oraclecloud17.com', Realm.OC17],
  ['ocistatus.us-dcc-phoenix-1.oci.oraclecloud17.com', Realm.OC17],

  ['ocistatus.oci.nricloud.jp', Realm.OC25],
  ['ocistatus.ap-dcc-tokyo-1.oci.nricloud.jp', Realm.OC25],

  ['ocistatus.oci.oraclecloud15.com', Realm.OC15],
  ['ocistatus.ap-dcc-gazipur-1.oci.oraclecloud15.com', Realm.OC15],

  ['ocistatus.oci.jpsovereigncloud.jp', Realm.OC40],
  ['ocistatus.ap-tatebayashi-1.oci.jpsovereigncloud.jp', Realm.OC40],

  ['ocistatus.oci.opencanvas.jp', Realm.OC50],
  ['ocistatus.ap-mitaka-1.oci.opencanvas.jp', Realm.OC50],

  ['ocistatus.oci.oraclecloud51.com', Realm.OC51],
  ['ocistatus.eu-budapest-1.oci.oraclecloud51.com', Realm.OC51],
]);

export const customBrandedRealms = new Set<Realm>([
  Realm.OC17,
  Realm.OC22,
  Realm.OC26,
  Realm.OC31,
  Realm.OC35,
  Realm.OC36,
  Realm.OC40,
  Realm.OC41,
  Realm.OC50,
  Realm.OC51,
  Realm.OC15,
]);

export const isCustomBrandedRealm = (hostname: string): boolean => {
  const realm = hostnameToRealm.get(window.location.hostname);
  if (realm === undefined) {
    return false;
  }
  return customBrandedRealms.has(realm);
};

export const getTopLevel = (region: string): string => {
  const regionEnum = region as Region;
  const realm = RegionToRealmMap[regionEnum];
  return realmTopLevels.get(realm) || '';
};

export const getSanitizedRegion = (regionName: string): string => {
  return regionName.toLowerCase() === 'us-seattle-1' ? 'r1' : regionName;
};

export const buildServiceUrl = (
  serviceName: string,
  region: string,
  secondLevelDomain: string,
  version?: string,
  pattern?: string,
): string => {
  mustNotEmptyNullOrUndefined('serviceName', serviceName);
  mustNotEmptyNullOrUndefined('secondLevelDomain', secondLevelDomain);

  // take care of r1 special case where it doesn't use region name as sub-domain
  let serviceUrl =
    pattern || 'https://{serviceName}.{region}.{secondLevelDomain}/{version}';

  serviceUrl = serviceUrl
    .replace(/{serviceName}/gi, serviceName)
    .replace(/{secondLevelDomain}/gi, secondLevelDomain)
    .replace(/{version}/gi, version || '')
    .replace(/\/$/, ''); // remove trailing "/"

  return region
    ? serviceUrl.replace(/{region}/gi, getSanitizedRegion(region))
    : serviceUrl.replace(/{region}\./gi, '');
};

/**
 * Returns the Login URL for the given regionName
 * @param regionName
 * @returns string
 */
export const buildLoginUrl = (regionName: string): string => {
  /**
   * Add a special case to point SOUP to beta unstable for r1-staging.
   * This is done to enable better testing in unstable regions
   */

  return buildServiceUrl(
    SERVICE_LOGIN,
    regionName,
    getTopLevel(regionName),
    ENDPOINT_VERSION_1,
  );
};

/**
 * Returns the Console URL for the given regionName
 * @param regionName
 * @returns string
 */
export const buildConsoleUrl = (regionName: string): string => {
  return buildServiceUrl(SERVICE_CONSOLE, regionName, getTopLevel(regionName));
};

/**
 * Returns the Identity URL for the given regionName
 * @param regionName
 * @returns string
 */
export const buildIdentityUrl = (regionName: string): string => {
  return buildServiceUrl(
    SERVICE_IDENTITY,
    regionName,
    getTopLevel(regionName),
    ENDPOINT_VERSION_20160918,
  );
};

/**
 * Returns the Authorization URL for the given regionName
 * @param regionName
 * @returns string
 */
export const buildAuthorizationUrl = (regionName: string): string => {
  return buildServiceUrl(
    SERVICE_AUTHENTICATION,
    regionName,
    getTopLevel(regionName),
    ENDPOINT_VERSION_1,
  );
};
